import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { motion, AnimatePresence } from "framer-motion";
import { Star, RotateCcw, Volume2, Mic, MicOff } from "lucide-react";
import { type Difficulty, getDifficultyConfig } from "./difficulty";
import { useSpeech } from "@/hooks/useSpeech";

const EMOJIS = ["🍎", "🌟", "🐟", "🦋", "🌸", "🍌", "🐤", "🎈", "🍒", "⚽"];

const shuffleArray = <T,>(arr: T[]): T[] => [...arr].sort(() => Math.random() - 0.5);

const generateRound = () => {
  const count = Math.floor(Math.random() * 9) + 1; // 1-9
  const emoji = EMOJIS[Math.floor(Math.random() * EMOJIS.length)];
  const wrongOptions = new Set<number>();
  while (wrongOptions.size < 3) {
    const n = Math.floor(Math.random() * 9) + 1;
    if (n !== count) wrongOptions.add(n);
  }
  return { count, emoji, options: shuffleArray([...wrongOptions, count]) };
};

const arabicNumber = (n: number) => {
  const map = ["٠", "١", "٢", "٣", "٤", "٥", "٦", "٧", "٨", "٩"];
  return String(n).split("").map((d) => map[parseInt(d)]).join("");
};

interface Props {
  onComplete?: (result: { score: number; maxScore: number; stars: number }) => void;
  difficulty?: Difficulty;
}

export default function NumbersGame({ onComplete, difficulty = "medium" }: Props) {
  const { rounds: totalRounds, timeout } = getDifficultyConfig(difficulty);
  const [round, setRound] = useState(0);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [data, setData] = useState(generateRound);
  const [selected, setSelected] = useState<number | null>(null);
  const [isCorrect, setIsCorrect] = useState<boolean | null>(null);
  const [completed, setCompleted] = useState(false);

  const { speak, isListening, startListening, stopListening, encourageCorrect, encourageWrong } = useSpeech({
    onVoiceResult: (transcript) => {
      if (selected !== null || round >= totalRounds) return;
      const arabicToNum: Record<string, string> = { "٠": "0", "١": "1", "٢": "2", "٣": "3", "٤": "4", "٥": "5", "٦": "6", "٧": "7", "٨": "8", "٩": "9" };
      const normalized = transcript.replace(/[٠-٩]/g, d => arabicToNum[d] || d);
      const nums = normalized.match(/\d+/g);
      if (nums) {
        const spoken = parseInt(nums[0]);
        if (data.options.includes(spoken)) handleSelect(spoken);
      }
    },
  });

  const handleSelect = (n: number) => {
    if (selected !== null) return;
    setSelected(n);
    const correct = n === data.count;
    setIsCorrect(correct);
    if (correct) {
      setScore((s) => s + 10);
      setStreak((s) => s + 1);
      encourageCorrect();
    } else {
      setStreak(0);
      encourageWrong();
    }
    setTimeout(() => {
      setSelected(null);
      setIsCorrect(null);
      setRound((r) => r + 1);
      setData(generateRound());
    }, timeout);
  };

  const reset = () => {
    setScore(0);
    setStreak(0);
    setRound(0);
    setData(generateRound());
    setSelected(null);
    setIsCorrect(null);
    setCompleted(false);
  };

  useEffect(() => {
    if (round >= totalRounds && !completed) {
      setCompleted(true);
      const maxScore = totalRounds * 10;
      const starCount = Math.ceil((score / maxScore) * 5);
      onComplete?.({ score, maxScore, stars: starCount });
    }
  }, [round]);

  if (round >= totalRounds) {
    return (
      <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
        <Card className="max-w-md mx-auto text-center shadow-elevated">
          <CardContent className="p-8 space-y-6">
            <motion.div animate={{ rotate: [0, -10, 10, 0] }} transition={{ repeat: Infinity, duration: 2 }} className="text-6xl">
              🎯
            </motion.div>
            <h2 className="text-2xl font-bold text-foreground">ممتاز! 🎉</h2>
            <p className="text-muted-foreground">حصلت على <span className="text-primary font-bold text-xl">{score}</span> نقطة</p>
            <div className="flex justify-center gap-1">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} className={`w-8 h-8 ${i < Math.ceil((score / (totalRounds * 10)) * 5) ? "text-yellow-400 fill-yellow-400" : "text-muted"}`} />
              ))}
            </div>
            <Button onClick={reset} size="lg" className="gradient-hero border-0 text-primary-foreground">
              <RotateCcw className="w-4 h-4 ml-2" />
              اللعب مرة أخرى
            </Button>
          </CardContent>
        </Card>
      </motion.div>
    );
  }

  return (
    <div className="max-w-lg mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <Badge variant="secondary" className="text-sm px-3 py-1">⭐ {score} نقطة</Badge>
        <Badge variant="outline" className="text-sm px-3 py-1">{round + 1} / {totalRounds}</Badge>
        {streak >= 2 && (
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }}>
            <Badge className="bg-gradient-to-r from-orange-400 to-red-500 text-white border-0 text-sm px-3 py-1">🔥 سلسلة {streak}</Badge>
          </motion.div>
        )}
      </div>

      <div className="h-2 bg-muted rounded-full overflow-hidden">
        <motion.div className="h-full rounded-full bg-gradient-to-r from-blue-400 to-indigo-500" animate={{ width: `${((round + 1) / totalRounds) * 100}%` }} />
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={round}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          transition={{ type: "spring", stiffness: 200 }}
        >
          <Card className="shadow-elevated overflow-hidden">
            <CardContent className="p-0">
              <div className="bg-gradient-to-br from-blue-400 to-indigo-500 p-6 text-center text-white relative">
                <div className="absolute top-3 left-3 flex gap-1">
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-white/80 hover:text-white hover:bg-white/20 rounded-full" onClick={() => speak(`كم عدد الأشياء؟`)}>
                    <Volume2 className="w-4 h-4" />
                  </Button>
                  <Button variant={isListening ? "destructive" : "ghost"} size="icon" className={`h-8 w-8 rounded-full ${isListening ? "animate-pulse" : "text-white/80 hover:text-white hover:bg-white/20"}`} onClick={() => isListening ? stopListening() : startListening()} disabled={selected !== null}>
                    {isListening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                  </Button>
                </div>
                <p className="text-lg font-medium mb-4 opacity-90">كم عدد الـ {data.emoji} ؟</p>
                <div className="flex flex-wrap justify-center gap-3 max-w-xs mx-auto">
                  {Array.from({ length: data.count }).map((_, i) => (
                    <motion.span
                      key={i}
                      className="text-4xl"
                      initial={{ opacity: 0, scale: 0, rotate: -180 }}
                      animate={{ opacity: 1, scale: 1, rotate: 0 }}
                      transition={{ delay: i * 0.08, type: "spring", stiffness: 300 }}
                    >
                      {data.emoji}
                    </motion.span>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </AnimatePresence>

      <div className="grid grid-cols-2 gap-3">
        {data.options.map((opt, i) => {
          const isSelected = selected === opt;
          const showResult = selected !== null;
          const isAnswer = opt === data.count;
          let bgClass = "bg-card hover:bg-accent border-2 border-border hover:border-primary/50";
          if (showResult && isAnswer) bgClass = "bg-emerald-100 border-2 border-emerald-500 dark:bg-emerald-900/30";
          else if (isSelected && !isAnswer) bgClass = "bg-red-100 border-2 border-red-400 dark:bg-red-900/30";

          return (
            <motion.button
              key={opt}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.08 }}
              whileHover={!selected ? { scale: 1.05 } : {}}
              whileTap={!selected ? { scale: 0.95 } : {}}
              onClick={() => handleSelect(opt)}
              disabled={!!selected}
              className={`rounded-xl p-5 text-center transition-all ${bgClass}`}
            >
              <span className="text-3xl font-bold text-foreground block">{arabicNumber(opt)}</span>
              <span className="text-lg text-muted-foreground">{opt}</span>
            </motion.button>
          );
        })}
      </div>
    </div>
  );
}
